import { ERROR } from './constants'
import { getCloudbaseContext, parseContext, getServerInjectUrl } from './context'
import { validateUid, E } from './utils'
import {
  callWxOpenApi,
  callCompatibleWxOpenApi,
  callWxPayApi,
  wxCallContainerApi,
  getWxOptions,
  type ISendWxRequestOptions,
} from './wx'
import type {
  ICreateTicketOpts,
  IGetUserInfoResult,
  IGetEndUserInfoResult,
  IUserInfoQuery,
  ITemplateNotifyReq,
  ICustomReqOpts,
  ICompleteCloudbaseContext,
} from './types'
import { ICloudbaseConfig } from '@cloudbase/types'

// 延迟加载 jsonwebtoken，避免非 Node 环境打包时引入此包
let jwtSign: ((...args: any[]) => any) | null = null
async function getJwtSign() {
  if (!jwtSign) {
    try {
      // @ts-ignore — 该包仅在 Node 运行时存在，开发环境可能未安装
      const mod = await import('jsonwebtoken')
      jwtSign = mod.default?.sign || mod.sign
    } catch (e) {
      console.error('缺少依赖 jsonwebtoken，请执行以下命令安装：\n\n'
        + '  npm install jsonwebtoken\n\n'
        + '该依赖用于 Node 环境下的自定义登录票据生成。')
    }
  }
  return jwtSign!
}

// ========== 辅助函数 ==========

/**
 * 从云函数运行时上下文中获取当前请求的用户信息
 */
function getDefaultUserInfo(): IGetUserInfoResult {
  const { WX_OPENID, WX_APPID, TCB_UUID, TCB_CUSTOM_USER_ID, TCB_ISANONYMOUS_USER } = getCloudbaseContext()

  return {
    openId: WX_OPENID || '',
    appId: WX_APPID || '',
    uid: TCB_UUID || '',
    customUserId: TCB_CUSTOM_USER_ID || '',
    isAnonymous: TCB_ISANONYMOUS_USER === 'true',
  }
}

/**
 * 调用后端管理接口查询用户信息
 */
function sendUserInfoRequest(app: any, params: Record<string, any>): Promise<any> {
  return app?.request?.send?.('auth.getUserInfoForAdmin', params, {
    pathname: 'web',
    endPointMode: 'CLOUD_API',
  })
}

// ========== scfContext 存储（静态） ==========

let _scfContext: any = null

// ========== nodeTool 主函数 ==========

/**
 * 初始化 Node 端全部能力，直接挂载到 js-sdk app 实例上
 * 包含：auth 扩展方法、微信 API、文件操作增强、callFunction/callContainer 增强、
 *       模板消息推送、context 解析等
 *
 * @param app - js-sdk cloudbase 实例（init 返回值）
 * @param config - 配置信息
 */
export const nodeTool = (app: any, config: ICloudbaseConfig) => {
  const nodeConfig = config as any

  // ========== parseContext / getCloudbaseContext ==========

  app.parseContext = (context: any) => {
    const parseResult = parseContext(context)
    _scfContext = parseResult
    return parseResult
  }

  app.getCloudbaseContext = (context?: any): ICompleteCloudbaseContext => getCloudbaseContext(context)

  // ========== auth 扩展 ==========

  if (app.auth) {
    const authMethods: Record<string, any> = {
      /** 获取当前请求的用户信息（从环境变量读取，同步） */
      getUserInfo(): IGetUserInfoResult {
        return getDefaultUserInfo()
      },

      /**
       * 获取终端用户信息
       * 不传 uid 时返回当前请求用户信息，传 uid 时查询指定用户
       */
      async getEndUserInfo(uid?: string): Promise<IGetEndUserInfoResult> {
        const defaultUserInfo = getDefaultUserInfo()

        if (uid === undefined) {
          return { userInfo: defaultUserInfo }
        }
        validateUid(uid)

        return sendUserInfoRequest(app, {
          uuid: uid,
          envName: config.env,
        }).then((result: any) => {
          if (result.code) {
            return result
          }
          return {
            userInfo: { ...defaultUserInfo, ...result.data },
            requestId: result.requestId,
          }
        })
      },

      /**
       * 创建自定义登录 Ticket
       */
      async createTicket(uid: string, options: ICreateTicketOpts = {}): Promise<string> {
        validateUid(uid)

        const timestamp = new Date().getTime()
        const { credentials } = config.auth || {}
        const { env } = config

        if (!env) {
          throw { ...ERROR.INVALID_PARAM, message: 'no env in config' }
        }

        if (!credentials?.env_id) {
          throw {
            ...ERROR.INVALID_PARAM,
            message: '当前私钥未包含env_id 信息， 请前往腾讯云云开发控制台，获取自定义登录最新私钥',
          }
        }

        if (credentials.env_id !== env) {
          throw {
            ...ERROR.INVALID_PARAM,
            message: '当前私钥所属环境与 init 指定环境不一致！',
          }
        }

        const {
          refresh = 3600 * 1000,
          expire = timestamp + 7 * 24 * 60 * 60 * 1000,
        } = options
        const sign = await getJwtSign()
        const token = sign(
          {
            alg: 'RS256',
            env,
            iat: timestamp,
            exp: timestamp + 10 * 60 * 1000,
            uid,
            refresh,
            expire,
          },
          credentials.private_key,
          {
            allowInsecureKeySizes: true,
            algorithm: 'RS256',
          },
        )

        return `${credentials.private_key_id}/@@/${token}`
      },

      /**
       * 按条件查询用户信息（管理端接口）
       */
      async queryUserInfo(query: IUserInfoQuery): Promise<any> {
        const { uid, platform, platformId } = query
        return sendUserInfoRequest(app, {
          uuid: uid,
          platform,
          platformId,
          envName: config.env,
        }).then((result: any) => {
          if (result.code) {
            return result
          }
          return {
            userInfo: { ...result.data },
            requestId: result.requestId,
          }
        })
      },

      /** 获取客户端 IP 地址 */
      getClientIP(): string {
        const { TCB_SOURCE_IP } = getCloudbaseContext()
        return TCB_SOURCE_IP || ''
      },

      /** 获取 Auth Context（从运行环境中解析） */
      getAuthContext(context?: any) {
        const fullContext = getCloudbaseContext(context) as any
        const { TCB_UUID, LOGINTYPE, QQ_OPENID, QQ_APPID } = fullContext
        const result: any = {
          uid: TCB_UUID,
          loginType: LOGINTYPE,
        }
        if (LOGINTYPE === 'QQ-MINI') {
          result.appId = QQ_APPID
          result.openId = QQ_OPENID
        }
        return result
      },

      /** 获取客户端凭证 */
      async getClientCredential(opts?: ICustomReqOpts) {
        if (nodeConfig.accessKey || config.accessKey) {
          return nodeConfig.accessKey || config.accessKey
        }

        const origin = app.request.getBaseEndPoint('GATEWAY')
        const clientAuthPath = '/auth/v1/token/clientCredential'

        const res = await app.request.post(
          {
            url: `${origin}${clientAuthPath}`,
            data: { grant_type: 'client_credentials' },
            headers: { 'content-type': 'application/json' },
          },
          opts ? { timeout: opts.timeout } : undefined
        )

        return res.data
      },
    }

    /**
     * 挂载 auth 扩展方法
     *
     * app.auth 是一个 Proxy 包装的函数对象：
     * - 作为属性访问 app.auth.xxx 时，访问的是这个函数对象本身
     * - 作为函数调用 app.auth() 时，返回的是内部缓存的 authInstance（纯 Auth 实例）
     *
     * 两者是不同的对象，所以需要：
     * 1. 在 app.auth（函数对象）上挂载方法 —— 支持 app.auth.getUserInfo()
     * 2. 用 Proxy 包装 app.auth，在每次调用 app.auth() 时，把方法也同步到返回的 authInstance 上
     *    —— 支持 const auth = app.auth(); auth.getUserInfo()
     */
    const originalAuth = app.auth

    // 先在 app.auth 函数对象本身上挂载方法
    Object.keys(authMethods).forEach((key) => {
      originalAuth[key] = authMethods[key]
    })

    // 用 Proxy 拦截函数调用，确保 app.auth() 返回的实例也带有这些方法
    app.auth = new Proxy(originalAuth, {
      apply(target: any, thisArg: any, argArray: any[]) {
        const authInstance = Reflect.apply(target, thisArg, argArray)
        // 将扩展方法同步到 authInstance 上（如果还没有的话）
        if (authInstance && typeof authInstance === 'object') {
          Object.keys(authMethods).forEach((key) => {
            if (!(key in authInstance)) {
              authInstance[key] = authMethods[key]
            }
          })
        }
        return authInstance
      },
    })
  }

  // ========== sendTemplateNotification ==========

  app.sendTemplateNotification = (params: ITemplateNotifyReq, opts?: { timeout?: number }) => app?.callFunction?.(
    {
      name: 'lowcode-datasource',
      data: {
        methodName: 'callWedaApi',
        params: {
          action: 'PushNotifyMsg',
          data: {
            NotifyId: params.notifyId,
            Data: JSON.stringify(params.data),
            NotifyUsers: undefined,
            Url: params.url,
          },
        },
        mode: 'c',
      },
    },
    undefined,
    opts,
  )

  // ========== 微信 API ==========

  function _getWxOptions(): ISendWxRequestOptions {
    const serviceUrl = nodeConfig.serviceUrl || getServerInjectUrl() || undefined
    return getWxOptions(nodeConfig, _scfContext, serviceUrl || '', app.request)
  }

  app.callWxOpenApi = (wxOpenApiOptions: any, opts?: ICustomReqOpts) => callWxOpenApi(_getWxOptions(), wxOpenApiOptions, opts)

  app.callWxPayApi = (wxOpenApiOptions: any, opts?: ICustomReqOpts) => callWxPayApi(_getWxOptions(), wxOpenApiOptions, opts)

  app.wxCallContainerApi = (wxOpenApiOptions: any, opts?: ICustomReqOpts) => wxCallContainerApi(_getWxOptions(), wxOpenApiOptions, opts)

  app.callCompatibleWxOpenApi = (wxOpenApiOptions: any, opts?: ICustomReqOpts) => callCompatibleWxOpenApi(_getWxOptions(), wxOpenApiOptions, opts)

  // ========== getFileAuthority ==========

  app.getFileAuthority = async ({ fileList }: { fileList: Array<{ type: string; path: string }> }, opts?: ICustomReqOpts) => {
    const { LOGINTYPE } = getCloudbaseContext()
    if (!Array.isArray(fileList)) {
      throw E({
        ...ERROR.INVALID_PARAM,
        message: '[js-sdk] getCosFileAuthority fileList must be a array',
      })
    }

    if (
      fileList.some((file) => {
        if (!file?.path) return true
        if (!['READ', 'WRITE', 'READWRITE'].includes(file.type)) return true
        return false
      })
    ) {
      throw E({
        ...ERROR.INVALID_PARAM,
        message: '[js-sdk] getCosFileAuthority fileList param error',
      })
    }

    const { userInfo } = await app.auth.getEndUserInfo()
    const { openId, uid } = userInfo

    if (!openId && !uid) {
      throw E({
        ...ERROR.INVALID_PARAM,
        message: '[js-sdk] admin do not need getCosFileAuthority.',
      })
    }

    const params = {
      openId,
      uid,
      loginType: LOGINTYPE,
      fileList,
    }

    const res = await app.request.send(
      'storage.getFileAuthority',
      params,
      { pathname: 'web', endPointMode: 'GATEWAY' },
      opts ? { timeout: opts.timeout } : undefined
    )

    if (res.code) {
      throw E({ ...res, message: `[js-sdk] getCosFileAuthority failed: ${res.code}` })
    } else {
      return res
    }
  }

  // ========== callApis ==========

  app.callApis = async (callApiOptions: any, opts?: ICustomReqOpts) => {
    if (app.apis) {
      const { name, path: apiPath = '', method = 'POST', header = {}, body, token } = callApiOptions
      const apiMethod = (method || 'POST').toLowerCase()
      return app.apis[name]?.[apiMethod]?.({
        path: apiPath,
        method,
        headers: header,
        body,
        token,
      }, opts)
    }
    throw new Error('callApis is not supported in current configuration')
  }
}
